import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  TrendingUp, 
  Wallet, 
  Bell, 
  Settings, 
  LogOut,
  Menu,
  ChevronRight,
  Sparkles
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navigation = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, color: "#F2337B" },
  { name: "Order Supplies", path: "/ordering", icon: ShoppingCart, color: "#087DDF" },
  { name: "Order History", path: "/orders", icon: Package, color: "#9B59B6" },
  { name: "Sales", path: "/sales", icon: TrendingUp, color: "#2ECC71" },
  { name: "Cash Reporting", path: "/cash-reporting", icon: Wallet, color: "#F6D003" },
  { name: "Notifications", path: "/notifications", icon: Bell, color: "#FF6B35" },
];

const adminNav = [
  { name: "Admin Panel", path: "/admin", icon: Settings, color: "#34495E" },
];

export const Sidebar = ({ user, activePath }) => {
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotificationCount();
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const NavContent = () => (
    <div className="flex flex-col h-full bg-white">
      {/* Logo - Colorful */}
      <div className="p-6 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] rounded-2xl flex items-center justify-center shadow-lg shadow-[#F2337B]/30">
            <span className="text-white font-heading text-2xl">M</span>
          </div>
          <div>
            <h2 className="font-heading text-xl text-gradient-pink-yellow">MELA</h2>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#F6D003]" />
              Franchisee Portal
            </p>
          </div>
        </div>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto min-h-0 sidebar-scroll">
        {navigation.map((item) => {
          const isActive = activePath === item.path;
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setIsMobileOpen(false);
              }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                ${isActive 
                  ? 'text-white shadow-lg' 
                  : 'text-gray-600 hover:bg-gray-50'
                }
              `}
              style={isActive ? {
                background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                boxShadow: `0 8px 20px -5px ${item.color}40`
              } : {}}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`
                w-9 h-9 rounded-lg flex items-center justify-center
                ${isActive ? 'bg-white/20' : ''}
              `}
              style={!isActive ? { backgroundColor: `${item.color}15` } : {}}
              >
                <item.icon 
                  className="w-5 h-5" 
                  style={{ color: isActive ? 'white' : item.color }}
                />
              </div>
              <span className="flex-1 text-left font-medium">{item.name}</span>
              {item.name === "Notifications" && unreadCount > 0 && (
                <span className="bg-gradient-to-r from-[#F2337B] to-[#FF6B35] text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse-gentle">
                  {unreadCount}
                </span>
              )}
              {isActive && <ChevronRight className="w-4 h-4" />}
            </button>
          );
        })}

        {/* Admin Navigation */}
        {user.role === "admin" && (
          <>
            <div className="my-4 border-t border-gray-100" />
            <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Admin
            </p>
            {adminNav.map((item) => {
              const isActive = activePath === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setIsMobileOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${isActive 
                      ? 'text-white shadow-lg' 
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                  style={isActive ? {
                    background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                    boxShadow: `0 8px 20px -5px ${item.color}40`
                  } : {}}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`
                    w-9 h-9 rounded-lg flex items-center justify-center
                    ${isActive ? 'bg-white/20' : ''}
                  `}
                  style={!isActive ? { backgroundColor: `${item.color}15` } : {}}
                  >
                    <item.icon 
                      className="w-5 h-5" 
                      style={{ color: isActive ? 'white' : item.color }}
                    />
                  </div>
                  <span className="flex-1 text-left font-medium">{item.name}</span>
                  {isActive && <ChevronRight className="w-4 h-4" />}
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* User Profile - Clickable to go to Profile Page - Fixed at bottom */}
      <div className="p-4 border-t border-gray-100 flex-shrink-0">
        <button
          onClick={() => {
            navigate("/profile");
            setIsMobileOpen(false);
          }}
          className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-[#F2337B]/5 to-[#F6D003]/5 rounded-xl hover:from-[#F2337B]/10 hover:to-[#F6D003]/10 transition-all group"
          data-testid="profile-btn"
        >
          <Avatar className="h-11 w-11 ring-2 ring-[#F2337B]/20 ring-offset-2 group-hover:ring-[#F2337B]/40 transition-all">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] text-white font-bold">
              {user.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 text-left">
            <p className="font-semibold text-sm truncate group-hover:text-[#F2337B] transition-colors">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">View Profile →</p>
          </div>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar - Fixed with scroll */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-white border-r border-gray-100 shadow-xl shadow-gray-100/50 overflow-hidden">
        <NavContent />
      </aside>

      {/* Mobile Header - Colorful */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#F2337B] to-[#FF6B9D] rounded-xl flex items-center justify-center shadow-lg shadow-[#F2337B]/20">
            <span className="text-white font-heading text-lg">M</span>
          </div>
          <span className="font-heading text-xl text-gradient-pink-yellow">MELA</span>
        </div>
        
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-xl hover:bg-[#F2337B]/10"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6 text-[#F2337B]" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <NavContent />
          </SheetContent>
        </Sheet>
      </div>

      {/* Mobile Spacer */}
      <div className="lg:hidden h-16" />
    </>
  );
};

export default Sidebar;
